import {Component, computed, OnInit, signal, Signal} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Dialog} from '@angular/cdk/dialog';
import {Apollo} from 'apollo-angular';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, catchError, combineLatest, distinctUntilChanged, filter, forkJoin, map, of, switchMap, tap, skipWhile, finalize} from 'rxjs';
import {toSignal, toObservable} from '@angular/core/rxjs-interop';
import {Airline, PoItem, PoItemInput, ProcessStateEnum, Query as WorkflowQuery, Mutation as WorkflowMutation} from '../../../types-workflow';
import {File as RepoFile, Mutation as RepoMutation, Organization, Query as RepoQuery} from '../../../types-repository';
import gql from 'graphql-tag';
import {IsAllowedPipe} from '../../../pipes/has-action-pipe';
import {PO_DESTINATIONS} from '../../../target_systems';
import {FormsModule} from '@angular/forms';
import {ContextService} from '../../../services/context.service';
import {GET_SINGLE_PO_ITEM, PUBLISH_PO_ITEM, RESTART_WORKFLOW} from '../../../queries-workflow';
import {LogoAreaComponent, BreadCrumbs} from '../../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../../headline-section/headline-section.component';
import {JsonPipe} from '@angular/common';
import {environment} from '../../../../environments/environment';
import {ProcessLinkComponent} from '../../process-link/process-link.component';
import {DurationPipe} from '../../../pipes/duration.pipe';
import {S3LinkComponent} from '../../s3-link/s3-link.component';
import {RemovePipe} from '../../../pipes/remove-pipe';
import {UserProfileService} from '../../../services/user-profile.service';
import {CldatePipe} from '../../../pipes/cldate.pipe';
import {ProcessService} from '../../../services/process.service';
import {BannerService} from '../../../banner/banner.service';
import {ConfirmDialogComponent} from '../../../confirm-dialog/confirm-dialog.component';
import {TsaLogDialogComponent} from './tsa-log-dialog/tsa-log-dialog.component';
import {FileSizePipe} from '../../../pipes/file-size-pipe';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {MessageFieldComponent} from '../../do-button/message-field.component';
import {TooltipDirective} from '../../../tooltip.directive';

@Component({
  selector: 'app-po-item-detail',
  templateUrl: './po-item-details.component.html',
  styleUrls: ['./po-item-details.component.scss'],
  imports: [
    IsAllowedPipe,
    RouterLink,
    LogoAreaComponent,
    HeadlineSectionComponent,
    JsonPipe,
    ProcessLinkComponent,
    DurationPipe,
    S3LinkComponent,
    CldatePipe,
    FileSizePipe,
    FormsModule,
    DoButtonComponent,
    MessageFieldComponent,
    TooltipDirective
  ]
})
export class PoItemDetailComponent implements OnInit {
  poItemId: Signal<string>;
  poItem: Signal<PoItem | undefined>;
  encodeFiles: Signal<RepoFile[]>;
  // Produced files that have a TS-analyzer log present in the preview bucket,
  // keyed by file id, with the log content preloaded for the viewer dialog.
  tsaLogs: Signal<Record<string, { url: string, content: string }>>;
  organization: Signal<Organization>;
  airline: Signal<Airline>;
  breadCrumbs: Signal<BreadCrumbs>;
  loading = true;

  publishReports = computed(() => {
    const reports = this.poItem()?.publish_reports;
    if (!reports?.length) return [];
    return reports
      .map(r => typeof r === 'string' ? JSON.parse(r) : r)
      .sort((a: any, b: any) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''));
  });

  /**
   * Public URL of one published file, or null when the destination has none.
   *
   * Only `vod` publishes somewhere publicly readable: it copies to
   * `s3://<publish bucket>/castlabs-vod/<customer>/<po>/<po item>/` (see `destinations.py`), which the
   * CDN serves under the same path -- the very base the DASH/HLS links on this card are built from.
   * Every other destination delivers to a customer system we cannot link into.
   *
   * Segments are percent-encoded because PO names and filenames legitimately carry spaces, parentheses
   * and colons (an organization URN as PO prefix, "Movie (1).mp4" as a source name).
   *
   * Only files from the current delivery are linked -- see the index check below.
   */
  publishedFileUrl(filename: string, index: number): string | null {
    const item = this.poItem();
    if (!item || item.po_destination !== 'vod' || !filename) {
      return null;
    }
    // Rows at or below the separator were delivered by earlier workflows. A later publish overwrites
    // the same CDN path, so those filenames no longer describe what is served there -- linking them
    // would point at whatever replaced them, or at nothing.
    const firstPrevious = this.previousDeliveryIndex();
    if (firstPrevious >= 0 && index >= firstPrevious) {
      return null;
    }
    const segments = [item.customer, item.po?.po_name, item.po_item_id, filename];
    if (segments.some(segment => !segment)) {
      return null;
    }
    return `https://vod${this.envsuffix}.cp.castlabs.com/`
      + segments.map(segment => encodeURIComponent(segment)).join('/');
  }

  previousDeliveryIndex = computed(() => {
    const reports = this.publishReports();
    const startDate = this.poItem()?.publish_process?.start_date;
    if (!startDate || !reports.length) return -1;
    return reports.findIndex((r: any) => r.completed_at < startDate);
  });

  protected readonly PO_DESTINATIONS = PO_DESTINATIONS;
  publish_single_po_inflight = false;
  restart_single_po_inflight = false;

  // File management state
  editingId: string | null = null;
  editingName: string = '';
  renameInflight = signal<string | null>(null);
  deleteInflight = signal<string | null>(null);
  deleteConfirmId: string | null = null;
  private refreshEncodeFiles$ = new BehaviorSubject<void>(undefined);
  copiedVtkJobId = signal<string | null>(null);
  copiedFileId = signal<string | null>(null);

  // Properties from PoItemDetailsComponent
  public VTK_SERVER = environment.VTK;
  envsuffix = environment.env_name === 'prod' ? '' : `-${environment.env_name}`;
  staff: Signal<boolean>;
  tablename: string;
  hasVideoTrack = computed(() =>
    this.poItem()?.tracks?.some(t => t.codec_type === 'video') ?? false
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: Dialog,
    private apollo: Apollo,
    private contextService: ContextService,
    userProfileService: UserProfileService,
    private processService: ProcessService,
    private banner: BannerService,
    private http: HttpClient
  ) {
    this.organization = this.contextService.organization;
    this.airline = this.contextService.airline;
    this.tablename = environment.poitems_table;
    this.staff = userProfileService.staff;

    const po_name$ = this.route.params.pipe(
      map(params => params['po']),
      distinctUntilChanged()
    );

    const po_item_id$ = this.route.params.pipe(
      map(params => params['poitem']),
      distinctUntilChanged()
    );

    this.poItemId = toSignal(po_item_id$, {initialValue: ''});

    const poItemData$ = combineLatest([
      po_name$,
      po_item_id$,
      toObservable(this.organization).pipe(filter((o): o is Organization => !!o))
    ]).pipe(
      tap(() => this.loading = true),
      switchMap(([po_name, po_item_id, org]) => {
        return this.apollo.use('workflow').watchQuery<WorkflowQuery>({
          query: GET_SINGLE_PO_ITEM(org.id),
          nextFetchPolicy: 'cache-and-network',
          pollInterval: 10000,
          variables: {po_name, po_item_id}
        }).valueChanges.pipe(
          filter((result): result is typeof result & {data: WorkflowQuery} => !!result.data?.list_POs)
        );
      }),
      tap(() => {
        this.loading = false;
      })
    );

    this.poItem = toSignal(
      poItemData$.pipe(
        map(result => {
          const poItems = result.data.list_POs?.pos?.[0]?.poitems || [];
          return poItems[0];
        })
      )
    );

    const FOLDER_QUERY = gql`
      query FolderFiles($id: ID!) {
        folder(id: $id) {
          files { id name size last_modified }
          folders { id name files { id name size last_modified } }
        }
      }
    `;

    this.encodeFiles = toSignal(
      combineLatest([
        toObservable(this.poItem).pipe(
          map(item => ({loc: item?.encode_location, done: item?.checkpoint_encodes_done})),
          distinctUntilChanged((a, b) => a.loc === b.loc && a.done === b.done),
        ),
        this.refreshEncodeFiles$,
      ]).pipe(
        switchMap(([{loc}]) => {
          if (!loc) return of([]);
          return this.apollo.query<RepoQuery>({
            query: FOLDER_QUERY,
            variables: { id: loc },
            fetchPolicy: 'network-only',
          }).pipe(
            map(res => {
              const folder = res.data.folder;
              const files: RepoFile[] = [...folder.files];
              for (const subfolder of folder.folders) {
                for (const file of subfolder.files) {
                  files.push({...file, name: subfolder.name + '/' + file.name});
                }
              }
              return files.sort((a, b) => a.name.localeCompare(b.name));
            }),
            catchError(() => of([]))
          );
        })
      ),
      {initialValue: []}
    );

    // Probe the preview bucket for a `<produced file>.tsa.log` next to the preview
    // manifest, and only surface a "TS Analysis" action for files whose log
    // actually exists. The content is fetched here (small text report) and reused
    // by the viewer dialog, so the log is downloaded only once.
    this.tsaLogs = toSignal(
      combineLatest([
        toObservable(this.encodeFiles),
        toObservable(this.poItem).pipe(
          map(item => PoItemDetailComponent.previewFolderUrl(item)),
          distinctUntilChanged()
        ),
      ]).pipe(
        switchMap(([files, dir]) => {
          if (!dir) return of({});
          const candidates = files.filter(f => PoItemDetailComponent.isTsFile(f.name));
          if (candidates.length === 0) return of({});
          return forkJoin(
            candidates.map(f => {
              const url = `${dir}/${f.name}.tsa.log`;
              return this.http.get(url, {responseType: 'text'}).pipe(
                map(content => ({id: f.id, url, content})),
                catchError(() => of(null))
              );
            })
          ).pipe(
            map(results => {
              const logs: Record<string, { url: string, content: string }> = {};
              for (const r of results) {
                if (r) logs[r.id] = {url: r.url, content: r.content};
              }
              return logs;
            })
          );
        })
      ),
      {initialValue: {}}
    );
  }

  ngOnInit() {
    this.breadCrumbs = computed(() => {
      const poItem = this.poItem();
      const org_id = this.organization()?.id;
      const airline = this.airline();
      if (!poItem || !org_id) {
        return [];
      }

      const po_name = poItem.po.po_name;
      const airlineRoute = poItem.po.airline;

      return [
        {
          display: 'Content Platform',
          commands: ['/o', org_id],
        },
        {
          display: 'Workflows',
          commands: ['/o', org_id, 'workflows'],
        },
        {
          display: airline?.airline_name || airlineRoute,
          commands: ['/o', org_id, 'workflows', airlineRoute],
        },
        {
          display: po_name,
          commands: ['/o', org_id, 'workflows', airlineRoute, po_name],
        }
      ];
    });
  }

  downloadFile(file: RepoFile) {
    const mutation = gql`mutation GenerateDownloadUrl($encode_id: ID!) {
      generateDownloadUrl(input: {file_id: $encode_id}) { signed_url }
    }`;
    this.apollo.mutate<RepoMutation>({
      mutation,
      variables: { encode_id: file.id }
    }).subscribe({
      next: d => {
        const a = document.createElement('a');
        a.href = d.data.generateDownloadUrl.signed_url;
        a.click();
      },
      error: err => this.banner.open('Download failed: ' + err.message, ['dismiss'])
    });
  }

  confirmDelete(file: RepoFile) { this.deleteConfirmId = file.id; }
  cancelDelete() { this.deleteConfirmId = null; }

  deleteFile(file: RepoFile) {
    this.deleteConfirmId = null;
    this.deleteInflight.set(file.id);
    const mutation = gql`mutation DeleteEncode($encode_id: ID!) {
      delete(input: {id: $encode_id}) { file { id } }
    }`;
    this.apollo.mutate<RepoMutation>({
      mutation,
      variables: { encode_id: file.id }
    }).subscribe({
      next: () => { this.deleteInflight.set(null); this.refreshEncodeFiles$.next(); },
      error: err => { this.deleteInflight.set(null); this.banner.open('Delete failed: ' + err.message, ['dismiss']); }
    });
  }

  startRename(file: RepoFile) { this.editingId = file.id; this.editingName = file.name; }
  cancelRename() { this.editingId = null; this.editingName = ''; }

  saveRename(file: RepoFile) {
    if (this.editingName === file.name || !this.editingName.trim()) { this.cancelRename(); return; }
    const folderPath = file.id.substring(0, file.id.lastIndexOf('/') + 1);
    const newPath = folderPath + this.editingName;
    this.renameInflight.set(file.id);
    this.editingId = null;
    this.editingName = '';

    const mutation = gql`mutation RenameEncode($src: String!, $dest: String!) {
      move_file(input: {src: $src, dest: $dest}) { __typename id state message start_date end_date }
    }`;
    this.apollo.mutate<RepoMutation>({ mutation, variables: { src: file.id, dest: newPath } }).pipe(
      switchMap(res => this.processService.observeProcess(res.data.move_file)),
      skipWhile(process => process.state === ProcessStateEnum.InProgress)
    ).subscribe({
      next: process => {
        this.renameInflight.set(null);
        if (process.state === ProcessStateEnum.Success) { this.refreshEncodeFiles$.next(); }
        else { this.banner.open('Rename failed: ' + process.message, ['dismiss']); }
      },
      error: err => { this.renameInflight.set(null); this.banner.open('Rename failed: ' + err.message, ['dismiss']); }
    });
  }

  goToVtkJob(poItem: PoItem, vtk_job: {id: string, organization_urn: string}) {
    this.router.navigate(
      ['/o', this.organization()?.id, 'workflows', poItem.po!.airline, poItem.po!.po_name, poItem.po_item_id, 'vtk-jobs', vtk_job.id],
      {queryParams: {org_urn: vtk_job.organization_urn}}
    );
  }

  copyVtkJobId(id: string) {
    if (!navigator.clipboard?.writeText) {
      this.banner.open('Copy failed: clipboard API not available', ['dismiss']);
      return;
    }
    navigator.clipboard.writeText(id).then(
      () => {
        this.copiedVtkJobId.set(null);
        requestAnimationFrame(() => this.copiedVtkJobId.set(id));
      },
      err => this.banner.open('Copy failed: ' + (err?.message ?? String(err)), ['dismiss'])
    );
  }

  copyS3Path(file: RepoFile) {
    const poItem = this.poItem();
    if (!poItem) {
      return;
    }
    const path = 's3://' + poItem.output_brefix + file.name;
    if (!navigator.clipboard?.writeText) {
      this.banner.open('Copy failed: clipboard API not available', ['dismiss']);
      return;
    }
    navigator.clipboard.writeText(path).then(
      () => {
        this.copiedFileId.set(null);
        requestAnimationFrame(() => this.copiedFileId.set(file.id));
      },
      err => this.banner.open('Copy failed: ' + (err?.message ?? String(err)), ['dismiss'])
    );
  }

  // TS-container extensions the Manzanita TS analyzer produces a `.tsa.log` for.
  private static readonly TS_EXTS = ['.mpg', '.mpeg', '.ts', '.m2ts', '.m2t'];

  private static isTsFile(name: string): boolean {
    const lower = name.toLowerCase();
    return PoItemDetailComponent.TS_EXTS.some(ext => lower.endsWith(ext));
  }

  private static previewFolderUrl(poItem: PoItem | undefined): string | null {
    const preview = poItem?.preview;
    // A preview may carry only one of the two manifest URLs; both live in the same
    // preview folder, so either works to locate sibling files. Documented fallback.
    const manifest = preview?.dash_manifest_url ?? preview?.hls_manifest_url;
    if (!manifest) return null;
    return manifest.substring(0, manifest.lastIndexOf('/'));
  }

  canShowTsaLog(file: RepoFile): boolean {
    return !!this.tsaLogs()[file.id];
  }

  openTsaLog(file: RepoFile) {
    const log = this.tsaLogs()[file.id];
    if (!log) return;
    this.dialog.open(TsaLogDialogComponent, {
      data: { filename: file.name, content: log.content }
    });
  }

  canRename(file: RepoFile): boolean {
    return this.contextService.isAllowed(
      'contentplatform:repository:Mutation/MoveFile',
      `contentplatform:file:input.src/${file.id}`
    )();
  }

  canDownload(file: RepoFile): boolean {
    return this.contextService.isAllowed(
      'contentplatform:repository:Mutation/GenerateDownloadUrl',
      `contentplatform:folder:input.file_id/${file.id}`
    )();
  }

  publish_single_po() {
    const poItem = this.poItem();
    if (!poItem) {
      return;
    }

    const action = () => {
      this.publish_single_po_inflight = true;
      this.apollo.use('workflow').mutate<WorkflowMutation>({
        mutation: PUBLISH_PO_ITEM,
        variables: {
          po_name: poItem.po.po_name,
          po_item_id: poItem.po_item_id,
        }
      }).pipe(
        tap((res) => {

          const client = this.apollo.use('workflow').client;
          const cache = client.cache;
          cache.modify({
            id: cache.identify({__typename: 'POItem', id: poItem.id}),
            fields: {
              publish_process(existingPublishProcess, {toReference}) {
                return toReference(res.data.publishPoItem);
              },
            },
          });
        }),
        switchMap(d => this.processService.observeProcess(d.data.publishPoItem, {
          successMessage: `${poItem.po_item_id} published successfully.`,
          failureMessage: `${poItem.po_item_id} failed to publish.`,
          link: window.location.href
        })),
        skipWhile(process => process.state === ProcessStateEnum.InProgress),
        finalize(() => this.publish_single_po_inflight = false)
      ).subscribe({
        error: (error) => {
          this.banner.open(error, ['Close']);
        }
      });
    };

    if (poItem.publish_process &&
      (poItem.publish_process.state === ProcessStateEnum.InProgress ||
        poItem.publish_process.state === ProcessStateEnum.Success)) {
      let secondLine = '';
      if (poItem.publish_process.state === ProcessStateEnum.InProgress) {
        secondLine = 'This will restart an already running publish process.';
      }
      if (poItem.publish_process.state === ProcessStateEnum.Success) {
        secondLine = 'This will restart a completed publish process.';
      }
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          headline: 'Publish',
          message: `Do you really want to publish ${poItem.po_item_id}?<br/>${secondLine}`,
          actionName: 'Publish',
          action: action
        }
      });
    } else {
      action();
    }
  }

  copyToClipboard(btn: DoButtonComponent, value: string | object) {
    const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    navigator.clipboard.writeText(text).then(
      () => btn.done('Copied'),
      () => btn.done('Copy failed', {severity: 'cl-alert-danger'})
    );
  }

  restartWorkflow() {
    const poItem = this.poItem();
    if (!poItem) {
      return;
    }

    const action = () => {
      this.restart_single_po_inflight = true;
      this.apollo.use('workflow').mutate<WorkflowMutation>({
        mutation: RESTART_WORKFLOW,
        variables: {
          po_name: poItem.po.po_name,
          po_item_id: poItem.po_item_id
        } satisfies PoItemInput,
        update: (cache, {data}) => {
          // Ensure the process has a start_date (use current time as fallback)
          const processData = {
            __typename: 'WorkflowProcessPayload',
            id: data.restartPoItem.id,
            state: data.restartPoItem.state,
            message: data.restartPoItem.message,
            data: data.restartPoItem.data ?? null,
            start_date: data.restartPoItem.start_date || new Date().toISOString(),
            end_date: data.restartPoItem.end_date ?? null,
          };

          const org = this.organization();
          const query = GET_SINGLE_PO_ITEM(org.id);
          const variables = { po_name: poItem.po.po_name, po_item_id: poItem.po_item_id };

          // Use updateQuery to modify the cached query result - this triggers watchQuery
          cache.updateQuery({ query, variables }, (existingData: WorkflowQuery | null) => {
            if (!existingData?.list_POs?.pos?.[0]?.poitems?.[0]) {
              return existingData;
            }

            return {
              ...existingData,
              list_POs: {
                ...existingData.list_POs,
                pos: existingData.list_POs.pos.map((po, poIdx) => poIdx === 0 ? {
                  ...po,
                  poitems: po.poitems.map((item, itemIdx) => itemIdx === 0 ? {
                    ...item,
                    tracks: [],
                    checkpoint_content_uploaded: false,
                    checkpoint_content_complete: false,
                    checkpoint_encodes_done: false,
                    vtk_jobs: [],
                    preview: null,
                    publish_process: null,
                    publish_reports: [],
                    encode_location: null,
                    workflow_process: processData,
                  } : item)
                } : po)
              }
            };
          });

          // Stop spinner immediately after mutation succeeds
          this.restart_single_po_inflight = false;
        }
      }).pipe(
        switchMap(res => this.processService.observeProcess(res.data.restartPoItem, {
          successMessage: `${poItem.po_item_id} encoded successfully.`,
          failureMessage: `${poItem.po_item_id} failed to encode.`,
          link: window.location.href
        })),
        skipWhile(process => process.state === ProcessStateEnum.InProgress),
        finalize(() => this.restart_single_po_inflight = false)
      ).subscribe({
        error: error => {
          this.banner.open('Error restarting ' + poItem.po_item_id + ': ' + error.message, ['Close']);
        }
      });
    };

    if (poItem.workflow_process &&
      (poItem.workflow_process.state === ProcessStateEnum.InProgress ||
        poItem.workflow_process.state === ProcessStateEnum.Success)) {
      let secondLine = '';
      if (poItem.workflow_process.state === ProcessStateEnum.InProgress) {
        secondLine = 'This will restart an already running encode process.';
      }
      if (poItem.workflow_process.state === ProcessStateEnum.Success) {
        secondLine = 'This will restart a completed encode process.';
      }
      this.dialog.open(ConfirmDialogComponent, {
        data: {
          headline: 'Restart',
          message: `Do you really want to restart ${poItem.po_item_id}?<br/>${secondLine}`,
          actionName: 'Restart',
          action: action
        }
      });
    } else {
      action();
    }
  }
}
